import React, { useEffect, useState, useCallback } from "react";
import { Star, Trash2, Play as PlayIcon, Heart, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Super Admin control: moderate community clips — mark Staff Pick or remove.
export default function ClipsModeration() {
  const [clips, setClips] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/clips/admin?limit=200"); setClips(data.clips || []); }
    catch (e) { setClips([]); toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load clips"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleFeature = async (c) => {
    setBusy(c.id);
    try {
      const { data } = await api.post(`/clips/${c.id}/feature`);
      setClips((list) => list.map((x) => (x.id === c.id ? { ...x, featured: data.featured } : x)));
      toast.success(data.featured ? "Marked as Staff Pick ⭐" : "Removed from Staff Picks");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };

  const remove = async (c) => {
    if (!window.confirm("Remove this clip permanently?")) return;
    setBusy(c.id);
    try {
      await api.delete(`/clips/${c.id}`);
      setClips((list) => list.filter((x) => x.id !== c.id));
      toast.success("Clip removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(null); }
  };

  if (!clips) return <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading clips…</p>;

  return (
    <div data-testid="sa-clips-moderation">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Film className="w-5 h-5 text-fuchsia-400" /> Community Clips <span className="text-xs font-normal text-[hsl(var(--muted-foreground))]">({clips.length})</span></h3>
      </div>
      {clips.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">No clips published yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clips.map((c) => (
            <div key={c.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden" data-testid={`sa-clip-${c.id}`}>
              <div className="relative">
                {c.featured && (
                  <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-black flex items-center gap-1 shadow">
                    <Star className="w-3 h-3 fill-current" /> Staff Pick
                  </div>
                )}
                <video src={`${BACKEND}${c.url}`} controls muted playsInline preload="metadata" className="w-full bg-black block" style={{ aspectRatio: "16/9" }} />
              </div>
              <div className="p-3 space-y-2">
                <p className="text-sm font-semibold line-clamp-1">{c.caption || "A wild gameplay moment"}</p>
                <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                  <span>{c.cover_emoji || "🎮"}</span><span className="truncate">{c.game_title}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-[hsl(var(--muted-foreground))]">
                  <span className="truncate max-w-[100px]">by {c.uploader_name}</span>
                  <div className="flex items-center gap-2.5">
                    <span className="flex items-center gap-1"><PlayIcon className="w-3 h-3" /> {c.plays}</span>
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {c.likes}</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant={c.featured ? "default" : "outline"} disabled={busy === c.id} onClick={() => toggleFeature(c)}
                    className={`rounded-full gap-1 flex-1 text-xs ${c.featured ? "bg-amber-400 text-black hover:bg-amber-300" : ""}`} data-testid={`sa-clip-feature-${c.id}`}>
                    <Star className={`w-3.5 h-3.5 ${c.featured ? "fill-current" : ""}`} /> {c.featured ? "Unpin" : "Staff Pick"}
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === c.id} onClick={() => remove(c)}
                    className="rounded-full gap-1 text-xs text-rose-400 hover:bg-rose-500/10 border-rose-500/30" data-testid={`sa-clip-remove-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
